/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useState } from 'react';
import Text from '../elements/TextComponent';
import { motion } from 'framer-motion';
import Body from '../static/Body';
import Card from '../static/Card';
import RecentMessages from '../static/RecentMessages';
import CardRow from '../static/CardRow';
import LineChart from '../static/LineChart';
import BotViewTimeChart from '../static/BotViewTImeChart';
import BotEngagementGrade from '../static/BotEngagementGrade';
import BotSummaryCard from '../static/BotSummaryCard';
import { DashboardSummary, getDashboardSummary } from '../../api/assistantsApi';
import { useProduct } from '../../context/ProductContext';
// If you have a Card wrapper, you can swap the outer <section> with it

const useDashboardSummary = () => {
  const [dashboardData, setDashboardData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    const res = await getDashboardSummary();
    if (res.error) {
      setError(res.error);
    } else {
      setDashboardData(res.data || null);
      setError(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return {
    dashboardData,
    loading,
    error,
    refresh: fetchSummary,
  };
};






// -----------------------------
// Small helpers
// -----------------------------
const PURPLE = 'var(--primary)';
const SUBTEXT = '#555';

// -----------------------------
// Cute Mascot (SVG bot)
// -----------------------------
const CuteBot: React.FC<{ size?: number }> = ({ size = 56 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    <rect x="8" y="20" width="64" height="44" rx="12" fill={PURPLE} opacity="0.12" />
    <rect x="18" y="28" width="44" height="28" rx="8" fill={PURPLE} />
    <circle cx="30" cy="42" r="4" fill="#fff" />
    <circle cx="50" cy="42" r="4" fill="#fff" />
    <rect x="36" y="56" width="8" height="8" rx="2" fill={PURPLE} />
    <circle cx="40" cy="16" r="6" fill={PURPLE} />
  </svg>
);




// -----------------------------
// Main component
// -----------------------------
interface Props {
  onSelectAnalytics: (botId: string) => void;
  goToAssistants: () => void;
}

const Dashboard: React.FC<Props> = ({onSelectAnalytics, goToAssistants}) => {
  const { product, isProduct } = useProduct();
  const handleViewAnalytics = (botId: string) => {
      onSelectAnalytics(botId); // Bubble up to Dashboard
  };
  const handleViewAssistants = () => {
      goToAssistants(); // Bubble up to Dashboard
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dashboardData, loading, error, refresh } = useDashboardSummary();

  if (error) return <div>Error: {error}</div>;
  // if (!dashboardData) return <div>No data available</div>;

  return (
    <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 20 }}
    transition={{ duration: 0.3 }}
    >
      <div style={{ overflowY: 'auto', height: '100vh' }}>
        <Body>
            <Card width="92%">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <CuteBot />
              <div>
                <Text
                  text="Your Dashboard"
                  textAlign="left"
                  fontSize="28px"
                  fontWeight={800}
                  color="#030303"
                />
              </div>
            </div>
            <Text
                  textAlign="left"
                  text="Overview of your assistants and activity"
                  fontSize="14px"
                  color={SUBTEXT}
                />
          </Card>
          {loading || !dashboardData ? 
            <Card width='92%' height='600px'>
              <div
                style={{
                  backgroundColor: 'var(--tint)',
                  borderRadius: '20px',
                  padding: '40px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Montserrat',
                  fontSize: '20px',
                  fontWeight: 500,
                  color: 'var(--primary)',
                  textAlign: 'center',
                  margin: '20px auto',
                }}
              >
                Loading your dashboard...
              </div>
            </Card>
        :
          <>
            <CardRow>
                <Card width='25%' height='300px'>
                  <Text
                  textAlign="left"
                  text="Weekly visitors to all assistants"
                  fontSize="16px"
                  color={SUBTEXT}
                />
                <div className='margin-bottom-10'></div>
                <LineChart data={dashboardData.dailyVisitsLast7Days}></LineChart>
                {/* <LineChart data={data.weeklyMessages} /> */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                    {/* <Text text={formatDayLabel(data.weeklyMessages[0].date)} fontSize="12px" color={SUBTEXT} />
                    <Text text={formatDayLabel(data.weeklyMessages[6].date)} fontSize="12px" color={SUBTEXT} /> */}
                </div>
                </Card>

                <Card width='25%' height='300px'>
                  <Text
                  textAlign="left"
                  text="Average retention time per assistant (min)"
                  fontSize="16px"
                  color={SUBTEXT}
                />
                <div className='margin-bottom-10'></div>
                <BotViewTimeChart data={dashboardData.botViewTimes} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                    {/* <Text text={formatDayLabel(data.weeklyVisitors[0].date)} fontSize="12px" color={SUBTEXT} />
                    <Text text={formatDayLabel(data.weeklyVisitors[6].date)} fontSize="12px" color={SUBTEXT} /> */}
                </div>
                </Card>
                {/* <Card title="Subscription Status" right={
                data.billing.status === 'active_subscription' ? (
                    <a href={data.billing.manageUrl} target="_blank" rel="noreferrer">
                    <Button label="Manage" width="auto" height="36px" />
                    </a>
                ) : data.billing.status === 'weekly_access' ? (
                    <a href="/pricing">
                    <Button label="Renew" width="auto" height="36px" />
                    </a>
                ) : null
                }>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                    <StatBadge label="Plan" value={data.billing.planName || (data.billing.status === 'active_subscription' ? 'Monthly' : data.billing.status === 'weekly_access' ? 'Weekly' : '—')} />
                    <StatBadge label="Status" value={data.billing.status.replace('_', ' ')} />
                    <StatBadge label="Days Left" value={daysUntilExpire != null ? `${Math.max(0, daysUntilExpire)}d` : '—'} />
                </div>
                </Card> */}
                <Card width="38%" height='300px'>
                  <RecentMessages
                    recentMessages={dashboardData.recentMessages}
                    onViewClick={(botId) => handleViewAnalytics(botId)}
                  />
              </Card>
                {/* notifications and recent messages */}

            </CardRow>
            <CardRow>
              <Card width='70%' height='250px'>
                <BotEngagementGrade stats={{ views: dashboardData.allBotViews, threadsStarted: dashboardData.totalThreads, messagesSent: dashboardData.totalMessages}} />
              </Card>
              <Card width='20%' height='250px'>
                <BotSummaryCard count={dashboardData.totalBots} onManageClick={() => handleViewAssistants()} />
                </Card>
            </CardRow>
            </>
            }
            {/* <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            </div> */}
        </Body>
        </div>
     </motion.div>
  );
};

export default Dashboard;
